import { FC, ReactNode, useState } from "react";
import { clsx } from "clsx";
import { Button, Card, Dropdown, MenuProps, Modal, Tooltip, message } from "../../../design-system";
import { MoreOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import {
    NegotiationStatus,
    Project,
    ProjectAction,
    ProjectStatus,
    RfxStatus,
    canDo,
    isRfxProject,
    toProjectSummary,
} from "../../../types/ProjectTypes";
import { AnalysisWidget } from "./AnalysisWidget";
import { DocumentWidget } from "./DocumentWidget";
import { ProjectNameWidget } from "./ProjectNameWidget";
import { ProjectCategoryWidget } from "./ProjectCategoryWidget";
import { ProjectStatusCell } from "./ProjectStatusCell";
import { useOrgCategories } from "../../../common/useOrgCategories";
import { deleteProject, reassignProjectOwner } from "../../../common/api/ProjectClient";
import { getRelativeTime } from "../../date/DateOp";

import { handleError } from "../../../common/ErrorHandling";
import { DateTime } from "../../../common/components/DateTime";
import { OwnerCell } from "../../../common/components/OwnerCell";
import { SvgIcon } from "../../../common/components/SvgIcon";
import { CollaboratorsWidget } from "./CollaboratorsWidget";
import { ProjectTimeline } from "./ProjectTimeline";
import { useUserState } from "../../../common/UserContext";
import { VendorLogo } from "./VendorLogo";
import styles from "./ProjectCard.module.less";

interface ProjectCardProps {
    project: Project;
    selectedVersion?: number;
    onAnalysisSelect: (version: number) => void;
    onAnalysisUpdate?: () => void;
    onUpdate: () => void;
    showActions?: boolean;
    adminActionsMenuItems?: MenuProps["items"];
    noMarginBottom?: boolean;
}

/**
 * One field of the header: the label in small caps over the value. The
 * fields share the row's width in proportion to their `share`, like the
 * hub table's columns, so they all grow with the screen. A field whose
 * value speaks for itself (a name, a category) has no label and sits
 * centered on the card's height instead of hanging from the top. A `cellText`
 * field types its value like a hub table cell, for values that size
 * themselves from their container (a status, a relative time).
 */
const Field: FC<{ label?: string; share: number; cellText?: boolean; children: ReactNode }> = ({
    label,
    share,
    cellText,
    children,
}) => (
    <div
        className={clsx(styles.fieldFluid, !label && styles.fieldUnlabelled)}
        style={{ flexGrow: share }}
    >
        {label && <div className={styles.fieldLabel}>{label}</div>}
        <div className={clsx(styles.fieldValue, cellText && styles.fieldValueCell)}>{children}</div>
    </div>
);

/**
 * What the primary action reads: "Update" while the analysis is the next
 * step, then what the pipeline waits on from the user once it is ready,
 * closing a deal that is ready to sign or picking an RFx's vendor.
 */
const primaryActionLabel = (project: Project): string => {
    if (isRfxProject(project)) {
        return project.rfx?.status === RfxStatus.SELECT_VENDOR ? "Select vendor" : "Update";
    }
    return project.orderForm?.negotiationStatus === NegotiationStatus.READY_TO_SIGN ? "Close" : "Update";
};

export const ProjectCard: FC<ProjectCardProps> = ({
    project,
    selectedVersion,
    onAnalysisSelect,
    onAnalysisUpdate,
    onUpdate,
    showActions = false,
    adminActionsMenuItems,
    noMarginBottom = false,
}) => {
    const navigate = useNavigate();
    const { me } = useUserState();
    const [timelineOpen, setTimelineOpen] = useState(false);
    // Only orgs with their own taxonomy have a Category field.
    const categories = useOrgCategories();

    // The hub's list cells read a summary; the header shows the same values.
    const summary = toProjectSummary(project);
    const vendorName = summary.vendorName || summary.vendorKey || "";
    const canEdit = canDo(project, ProjectAction.EDIT);

    const handleDeleteProject = () => {
        Modal.confirm({
            title: "Delete project",
            content: `Are you sure you want to delete "${project.name || "this project"}"?`,
            okText: "Delete",
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await deleteProject(project.id);
                    message.success("Project deleted");
                    navigate("/project");
                } catch (error) {
                    handleError(error);
                }
            },
        });
    };

    const buildActionButtons = (): ReactNode[] => {
        if (!showActions) return [];

        const buttons: ReactNode[] = [
            <CollaboratorsWidget
                key="collaborators"
                project={project}
                currentUserId={me.id}
                onUpdate={onUpdate}
            />,
        ];

        // One menu for the rarer actions: the admin ones, then Delete for
        // whoever may delete (the owner or an admin), in any status.
        const items: MenuProps["items"] = [...(adminActionsMenuItems ?? [])];
        if (canDo(project, ProjectAction.DELETE)) {
            if (items.length) items.push({ type: "divider" });
            items.push({
                key: "delete",
                label: "Delete",
                danger: true,
                onClick: handleDeleteProject,
            });
        }
        const menu = items.length ? { items } : undefined;
        const showUpdate = canEdit
            && project.status !== ProjectStatus.DRAFT
            && project.status !== ProjectStatus.COMPLETED;

        // The primary action, with the menu behind a chevron next to it.
        if (showUpdate && menu) {
            buttons.push(
                <Dropdown.Button
                    key="update"
                    type="primary"
                    menu={menu}
                    trigger={["click"]}
                    onClick={onAnalysisUpdate}
                >
                    {primaryActionLabel(project)}
                </Dropdown.Button>,
            );
        } else if (showUpdate) {
            buttons.push(
                <Button key="update" type="primary" onClick={onAnalysisUpdate}>
                    {primaryActionLabel(project)}
                </Button>,
            );
        }

        // Without a primary action the menu keeps its own button.
        if (menu && !showUpdate) {
            buttons.push(
                <Dropdown key="more" menu={menu} trigger={["click"]}>
                    <Button aria-label="More actions" icon={<MoreOutlined />} />
                </Dropdown>,
            );
        }

        return buttons;
    };

    const actionButtons = buildActionButtons();

    return (
        <Card
            className={`${styles.headerCard} ${styles.headerCardWithLogo} ${isRfxProject(project) ? styles.headerCardRfx : ""} ${noMarginBottom ? styles.headerCardNoMarginBottom : ""}`}
        >
            <VendorLogo vendorHero={summary.vendorHero} vendorName={vendorName} projectName={project.name} />
            <div className={styles.headerContent}>
                <div className={styles.fieldRow}>
                    <Field share={15}>
                        <ProjectNameWidget
                            projectId={project.id}
                            name={project.name}
                            vendorName={vendorName || undefined}
                            canEdit={canEdit}
                            onUpdate={onUpdate}
                        />
                    </Field>

                    {categories && (
                        <Field share={18}>
                            <ProjectCategoryWidget
                                projectId={project.id}
                                categoryPath={project.categoryPath}
                                categories={categories}
                                canEdit={canEdit}
                                onUpdate={onUpdate}
                            />
                        </Field>
                    )}

                    <Field label="Owner" share={14}>
                        <OwnerCell
                            ownerId={project.ownerId}
                            ownerName={project.owner?.name}
                            canEdit={canDo(project, ProjectAction.REASSIGN)}
                            onSave={async (newOwnerId) => {
                                await reassignProjectOwner(project.id, newOwnerId);
                            }}
                            onUpdate={onUpdate}
                        />
                    </Field>

                    <Field label="Status" share={12} cellText>
                        <ProjectStatusCell project={summary} />
                    </Field>

                    <Field label="Analysis" share={13}>
                        <AnalysisWidget
                            project={project}
                            selectedVersion={selectedVersion}
                            canEdit={canEdit}
                            onAnalysisSelect={onAnalysisSelect}
                            onAction={onAnalysisUpdate}
                        />
                    </Field>

                    <Field label="Documents" share={10}>
                        <DocumentWidget
                            documents={project.documents || []}
                            canEdit={
                                canEdit &&
                                project.status !== ProjectStatus.COMPLETED &&
                                project.status !== ProjectStatus.DRAFT
                            }
                            onAction={onAnalysisUpdate}
                        />
                    </Field>

                    <Field label="Updated" share={10} cellText>
                        <Tooltip title={<DateTime date={project.lastUpdateDate} />}>
                            <button
                                className={styles.timelineButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setTimelineOpen(true);
                                }}
                            >
                                {getRelativeTime(project.lastUpdateDate)}
                                <SvgIcon name="arrow-up-right" size={12} />
                            </button>
                        </Tooltip>
                        <ProjectTimeline
                            project={project}
                            open={timelineOpen}
                            onClose={() => setTimelineOpen(false)}
                        />
                    </Field>

                    {actionButtons.length > 0 && (
                        <div className={styles.actionButtons} onClick={(e) => e.stopPropagation()}>
                            {actionButtons}
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};
